import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CALENDAR_ITEM_DRAG_MIME,
  CALENDAR_METRIC_DRAG_MIME,
  isCalendarItemDrag,
  parseCalendarItemDragPayload,
  setCalendarItemDragData,
  type CalendarItemDragPayload,
} from '../utils/calendarItemDrag';
import { isOsFileDrag } from '../utils/calendarFileDrop';
import type { CalendarActionTarget } from '../features/calendar/CalendarItemActionSheet';

interface Options {
  userId?: number | null;
  moveItemToDate: (target: CalendarActionTarget, targetIso: string) => Promise<boolean>;
}

function payloadToActionTarget(payload: CalendarItemDragPayload): CalendarActionTarget | null {
  switch (payload.kind) {
    case 'metric':
      if (!payload.uniqueCode) return null;
      return {
        kind: 'metric',
        uniqueCode: payload.uniqueCode,
        label: payload.label,
        dateParam: payload.sourceDate,
      };
    case 'health_document':
      if (!payload.healthDocumentId) return null;
      return {
        kind: 'health_document',
        id: payload.healthDocumentId,
        label: payload.label,
        uniqueCode: payload.uniqueCode ?? null,
        dateParam: payload.sourceDate,
      };
    case 'event':
      if (!payload.eventId) return null;
      return {
        kind: 'event',
        id: payload.eventId,
        label: payload.label,
        dateParam: payload.sourceDate,
      };
    case 'note':
      if (!payload.noteId) return null;
      return {
        kind: 'note',
        id: payload.noteId,
        label: payload.label,
        dateParam: payload.sourceDate,
      };
    default:
      return null;
  }
}

function readDragPayload(e: React.DragEvent): CalendarItemDragPayload | null {
  const raw =
    e.dataTransfer.getData(CALENDAR_ITEM_DRAG_MIME) ||
    e.dataTransfer.getData(CALENDAR_METRIC_DRAG_MIME);
  if (!raw) return null;
  const payload = parseCalendarItemDragPayload(raw);
  if (payload) return payload;
  try {
    const legacy = JSON.parse(raw) as {
      uniqueCode?: string;
      sourceDate?: string;
      sourceUserId?: number;
      label?: string;
    };
    if (legacy.uniqueCode && legacy.sourceDate) {
      return {
        kind: 'metric',
        uniqueCode: legacy.uniqueCode,
        sourceDate: legacy.sourceDate,
        sourceUserId: legacy.sourceUserId ?? 0,
        label: legacy.label ?? 'Metric',
      };
    }
  } catch {
    return null;
  }
  return null;
}

/** HTML5 drag-and-drop helpers for moving calendar items between days (desktop). */
export function useCalendarItemDragDrop({ userId, moveItemToDate }: Options) {
  const dragRef = useRef<CalendarItemDragPayload | null>(null);
  const [dragToken, setDragToken] = useState<string | null>(null);
  const [dropTargetDate, setDropTargetDate] = useState<string | null>(null);

  useEffect(() => {
    const onDragEnd = () => {
      dragRef.current = null;
      setDragToken(null);
      setDropTargetDate(null);
    };
    document.addEventListener('dragend', onDragEnd);
    return () => document.removeEventListener('dragend', onDragEnd);
  }, []);

  const getItemDragProps = useCallback(
    (payload: CalendarItemDragPayload) => {
      if (!userId) return {};

      const token =
        payload.kind === 'metric'
          ? payload.uniqueCode
          : payload.kind === 'health_document'
            ? `health-${payload.healthDocumentId}`
            : payload.kind === 'event'
              ? `event-${payload.eventId}`
              : payload.kind === 'note'
                ? `note-${payload.noteId}`
                : '';

      return {
        draggable: true,
        onDragStart: (e: React.DragEvent) => {
          setCalendarItemDragData(e, payload);
          dragRef.current = payload;
          setDragToken(token ?? null);
        },
        'data-dragging': dragToken === token ? 'true' : undefined,
      } as const;
    },
    [dragToken, userId],
  );

  const getMetricDragProps = useCallback(
    (uniqueCode: string, sourceDate: string, label: string) =>
      getItemDragProps({
        kind: 'metric',
        uniqueCode,
        sourceDate,
        sourceUserId: userId ?? 0,
        label,
      }),
    [getItemDragProps, userId],
  );

  const getHealthDocumentDragProps = useCallback(
    (healthDocumentId: number, sourceDate: string, label: string, uniqueCode?: string | null) =>
      getItemDragProps({
        kind: 'health_document',
        healthDocumentId,
        uniqueCode: uniqueCode ?? undefined,
        sourceDate,
        sourceUserId: userId ?? 0,
        label,
      }),
    [getItemDragProps, userId],
  );

  const getEventDragProps = useCallback(
    (eventId: number, sourceDate: string, label: string) =>
      getItemDragProps({
        kind: 'event',
        eventId,
        sourceDate,
        sourceUserId: userId ?? 0,
        label,
      }),
    [getItemDragProps, userId],
  );

  const getNoteDragProps = useCallback(
    (noteId: number, sourceDate: string, label: string) =>
      getItemDragProps({
        kind: 'note',
        noteId,
        sourceDate,
        sourceUserId: userId ?? 0,
        label,
      }),
    [getItemDragProps, userId],
  );

  const getDayDropProps = useCallback(
    (targetDateIso: string) => ({
      onDragOver: (e: React.DragEvent) => {
        if (isOsFileDrag(e)) return;
        if (!isCalendarItemDrag(e) && !dragRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        setDropTargetDate(targetDateIso);
      },
      onDragLeave: (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDropTargetDate(prev => (prev === targetDateIso ? null : prev));
        }
      },
      onDrop: async (e: React.DragEvent) => {
        if (isOsFileDrag(e)) return;

        const payload = readDragPayload(e) ?? dragRef.current;
        dragRef.current = null;
        setDragToken(null);
        setDropTargetDate(null);

        if (!payload) return;

        const target = payloadToActionTarget(payload);
        if (!target) return;

        e.preventDefault();
        e.stopPropagation();
        await moveItemToDate(target, targetDateIso);
      },
    }),
    [moveItemToDate],
  );

  const isDayDropTarget = useCallback(
    (targetDateIso: string) => dropTargetDate === targetDateIso,
    [dropTargetDate],
  );

  return {
    dragToken,
    getMetricDragProps,
    getHealthDocumentDragProps,
    getEventDragProps,
    getNoteDragProps,
    getItemDragProps,
    getDayDropProps,
    isDayDropTarget,
  };
}
